import { useRef, useState, useTransition } from "react";
import { Button } from "@bindery/ui/components/ui/button";
import { recordDrillEngagement } from "../../lib/api";

type ButtonKey = "up" | "down" | "bookmark" | "like" | "dismiss";

export function DrillEngagement({
  readingId,
  sourceUri,
  sourceCid,
}: {
  readingId: string;
  sourceUri: string | null;
  sourceCid: string | null;
}) {
  // Tracks the most recent vote (so up/down toggle each other visually) and
  // sticky activation for bookmark/dismiss. The in-flight ref blocks double-
  // clicks synchronously: two clicks within one event-loop tick would both
  // pass a React-state guard before setState flushes, so we mutate a ref
  // instead. useTransition's isPending is reserved for visual disable state.
  const [activated, setActivated] = useState<Set<ButtonKey>>(new Set());
  const [voteValue, setVoteValue] = useState<1 | -1 | null>(null);
  const inFlight = useRef<Set<ButtonKey>>(new Set());
  const [, startTransition] = useTransition();

  const dispatch = (key: ButtonKey) => {
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);

    if (key === "up" || key === "down") {
      const value: 1 | -1 = key === "up" ? 1 : -1;
      setVoteValue(value);
      setActivated((prev) => {
        const next = new Set(prev);
        next.add(key);
        next.delete(key === "up" ? "down" : "up");
        return next;
      });
    } else {
      setActivated((prev) => new Set(prev).add(key));
    }

    startTransition(async () => {
      try {
        if (key === "up") {
          await recordDrillEngagement(readingId, sourceUri, sourceCid, {
            kind: "vote",
            value: 1,
          });
        } else if (key === "down") {
          await recordDrillEngagement(readingId, sourceUri, sourceCid, {
            kind: "vote",
            value: -1,
          });
        } else if (key === "bookmark") {
          await recordDrillEngagement(readingId, sourceUri, sourceCid, {
            kind: "bookmark",
          });
        } else if (key === "like") {
          await recordDrillEngagement(readingId, sourceUri, sourceCid, {
            kind: "like",
          });
        } else {
          await recordDrillEngagement(readingId, sourceUri, sourceCid, {
            kind: "dismiss",
          });
        }
      } catch (err) {
        console.error("recordDrillEngagement failed:", err);
      } finally {
        inFlight.current.delete(key);
      }
    });
  };

  const isActive = (key: ButtonKey) => activated.has(key);

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant={isActive("up") && voteValue === 1 ? "default" : "outline"}
        size="sm"
        aria-pressed={isActive("up")}
        aria-label="Vote up"
        onClick={() => dispatch("up")}
      >
        👍
      </Button>
      <Button
        type="button"
        variant={isActive("down") && voteValue === -1 ? "default" : "outline"}
        size="sm"
        aria-pressed={isActive("down")}
        aria-label="Vote down"
        onClick={() => dispatch("down")}
      >
        👎
      </Button>
      <Button
        type="button"
        variant={isActive("bookmark") ? "default" : "outline"}
        size="sm"
        aria-pressed={isActive("bookmark")}
        aria-label="Bookmark"
        onClick={() => dispatch("bookmark")}
      >
        🔖
      </Button>
      <Button
        type="button"
        variant={isActive("like") ? "default" : "outline"}
        size="sm"
        aria-pressed={isActive("like")}
        aria-label="Like"
        onClick={() => dispatch("like")}
      >
        ❤️
      </Button>
      <Button
        type="button"
        variant={isActive("dismiss") ? "default" : "outline"}
        size="sm"
        aria-pressed={isActive("dismiss")}
        aria-label="Dismiss"
        onClick={() => dispatch("dismiss")}
      >
        ✕
      </Button>
    </div>
  );
}
